/**
 * @file Font loading optimization utilities
 * @description 
 * This file contains utilities for optimizing font loading and preventing layout shifts.
 * It implements best practices for font loading, including preloading, font-display swap,
 * and fallback font metrics.
 * 
 * Key features:
 * - Font preloading for critical fonts
 * - Font loading detection and class application
 * - Layout shift prevention with font metrics
 * - Fallback handling for browsers without Font Loading API
 * 
 * @dependencies
 * - None - Pure JavaScript utilities
 */

/**
 * Initialize font loading optimizations
 * This function should be called as early as possible in the application lifecycle
 */
export function initFontLoading(): void {
  if (typeof window === 'undefined') {
    return; // Skip in SSR context
  }

  // Add class to indicate fonts are not yet loaded
  document.documentElement.classList.add('fonts-not-loaded');

  // Check if the browser supports the Font Loading API
  if ('fonts' in document) {
    // Define the fonts we want to load and track
    const fontFamilies = [
      'raleway',
      'ivypresto-display'
    ];

    // Create an array of promises for each font to load
    const fontPromises = fontFamilies.map(family => 
      document.fonts.load(`1em ${family}`)
    );

    // When all fonts are loaded, update the class on the document
    Promise.all(fontPromises)
      .then(() => {
        document.documentElement.classList.remove('fonts-not-loaded');
        document.documentElement.classList.add('fonts-loaded');
        
        // Log success for debugging
        if (process.env.NODE_ENV !== 'production') {
          console.log('All fonts loaded successfully');
        }
      })
      .catch(error => {
        // Still add the fonts-loaded class to ensure content is visible
        document.documentElement.classList.remove('fonts-not-loaded');
        document.documentElement.classList.add('fonts-loaded');
        
        // Log error for debugging
        if (process.env.NODE_ENV !== 'production') {
          console.warn('Error loading fonts:', error);
        }
      });

    // Add a timeout to ensure the fonts-loaded class is added even if fonts fail to load
    setTimeout(() => {
      if (!document.documentElement.classList.contains('fonts-loaded')) {
        document.documentElement.classList.remove('fonts-not-loaded');
        document.documentElement.classList.add('fonts-loaded');
        
        if (process.env.NODE_ENV !== 'production') {
          console.warn('Font loading timed out, applying fonts-loaded class anyway');
        }
      }
    }, 3000); // 3 second timeout
  } else {
    // For browsers that don't support the Font Loading API, immediately add the fonts-loaded class
    document.documentElement.classList.remove('fonts-not-loaded');
    document.documentElement.classList.add('fonts-loaded');
  }
}

/**
 * Preload critical fonts
 * This function can be called to manually preload specific fonts
 * @param fontUrls - Array of font URLs to preload
 */
export function preloadFonts(fontUrls: string[]): void {
  if (typeof window === 'undefined') {
    return; // Skip in SSR context
  }

  fontUrls.forEach(url => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.href = url;
    link.as = 'font';
    link.type = url.endsWith('.woff2') ? 'font/woff2' : 
                url.endsWith('.woff') ? 'font/woff' : 
                'font/ttf';
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
  });
}

/**
 * Check if all fonts are loaded
 * @returns Boolean indicating if all fonts are loaded
 */
export function areFontsLoaded(): boolean {
  if (typeof window === 'undefined') {
    return false; // In SSR context, fonts are not loaded
  }
  
  return document.documentElement.classList.contains('fonts-loaded');
}

/**
 * Get appropriate font class based on loading status
 * Useful for conditionally applying styles based on font loading state
 * @returns CSS class name based on font loading state
 */
export function getFontLoadingClass(): string {
  if (typeof window === 'undefined') {
    return 'fonts-not-loaded'; // Default to not loaded in SSR
  }
  
  return document.documentElement.classList.contains('fonts-loaded') 
    ? 'fonts-loaded' 
    : 'fonts-not-loaded';
}

export default {
  initFontLoading,
  preloadFonts,
  areFontsLoaded,
  getFontLoadingClass
};